import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Sidebar from "@/components/Sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from "@/components/ui/dialog";
import { Ticket, Plus, Copy, Trash2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const InviteCodes = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newCodeNote, setNewCodeNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, codeId: null });
  const token = localStorage.getItem("token");

  const fetchCodes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/invite-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCodes(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied - Super admin only");
        navigate("/dashboard");
      } else if (error.response?.status === 401) {
        localStorage.removeItem("token");
        setUser(null);
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, setUser]);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  const handleCreateCode = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/invite-codes`,
        { note: newCodeNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invite code created: ${response.data.code}`);
      setCreateModalOpen(false);
      setNewCodeNote("");
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  const handleCopyLink = (code) => {
    const link = `${window.location.origin}/register?code=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Registration link copied to clipboard");
  };

  const handleDeleteCode = async (code) => {
    try {
      await axios.delete(`${API}/invite-codes/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invite code deleted");
      fetchCodes();
    } catch (error) {
      toast.error("Failed to delete code");
    }
  };

  const confirmDeleteCode = (code) => {
    setDeleteConfirm({ open: true, codeId: code });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
        <div className="flex-1 flex items-center justify-center ml-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  const activeCount = codes.filter(c => !c.used && !isExpired(c.expires_at)).length;
  const usedCount = codes.filter(c => c.used).length;

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="invite-codes-page">
      <Sidebar user={user} setUser={setUser} onLogout={handleLogout} />
      <div className="flex-1 p-8 ml-64">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Invite Codes</h1>
            <p className="text-slate-500 mt-1">Manage registration invite codes for new managers</p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="create-code-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Invite Code
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Total Codes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{codes.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-emerald-600">Active</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">Used</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{usedCount}</p>
            </CardContent>
          </Card>
        </div>

        {/* Codes List */}
        <Card>
          <CardHeader>
            <CardTitle>All Invite Codes</CardTitle>
            <CardDescription>Codes expire 5 days after creation</CardDescription>
          </CardHeader>
          <CardContent>
            {codes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No invite codes yet</p>
                <p className="text-sm">Create one to invite a new manager</p>
              </div>
            ) : (
              <div className="space-y-3">
                {codes.map((code) => (
                  <div 
                    key={code.code}
                    className={`p-4 rounded-lg border ${
                      code.used 
                        ? 'bg-slate-50 border-slate-200' 
                        : isExpired(code.expires_at)
                          ? 'bg-red-50 border-red-200'
                          : 'bg-white border-slate-200'
                    }`}
                    data-testid={`code-${code.code}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="font-mono text-lg font-bold tracking-wider">
                          {code.code}
                        </div>
                        {code.used ? (
                          <Badge className="bg-slate-100 text-slate-600">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Used
                          </Badge>
                        ) : isExpired(code.expires_at) ? (
                          <Badge className="bg-red-100 text-red-600">
                            <XCircle className="w-3 h-3 mr-1" />
                            Expired
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-600">
                            <Clock className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {!code.used && !isExpired(code.expires_at) && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyCode(code.code)}
                              data-testid={`copy-code-${code.code}`}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Code
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCopyLink(code.code)}
                              data-testid={`copy-link-${code.code}`}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Link
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => confirmDeleteCode(code.code)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-code-${code.code}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-slate-500 flex items-center gap-4">
                      <span>Created: {formatDate(code.created_at)}</span>
                      <span>Expires: {formatDate(code.expires_at)}</span>
                      {code.note && <span className="text-slate-400">• {code.note}</span>}
                    </div>
                    {code.used && code.used_by && (
                      <div className="mt-2 text-sm text-emerald-600">
                        Used by: {code.used_by}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Invite Code</DialogTitle>
              <DialogDescription>
                This code will allow someone to register as a new manager with their own team. 
                The code expires in 5 days.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="note">Note (optional)</Label>
                <Input
                  id="note"
                  placeholder="e.g., For John's team"
                  value={newCodeNote}
                  onChange={(e) => setNewCodeNote(e.target.value)}
                  data-testid="code-note-input"
                />
                <p className="text-xs text-slate-500">Add a note to remember who this code is for</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCode}
                disabled={creating}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-create-code-btn"
              >
                {creating ? "Creating..." : "Create Code"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Invite Code Confirmation */}
        <ConfirmDialog
          open={deleteConfirm.open}
          onOpenChange={(open) => setDeleteConfirm({ open, codeId: null })}
          title="Delete Invite Code?"
          description="Are you sure you want to delete this invite code? It can no longer be used for registration."
          confirmText="Delete"
          variant="destructive"
          onConfirm={() => handleDeleteCode(deleteConfirm.codeId)}
        />
      </div>
    </div>
  );
};

export default InviteCodes;
